import { useNavigate } from "react-router-dom";
import { Star } from "lucide-react";
import { applyPosterFallback, getEventId, getEventPoster } from "@/lib/eventDisplay";

export function MovieCard({ movie }) {
  const navigate = useNavigate();
  return (
    <button
      data-testid={`movie-card-${movie.movie_id}`}
      onClick={() => navigate(`/movie/${movie.movie_id}`)}
      className="group text-left flex-shrink-0 w-44 md:w-56"
    >
      <div className="relative aspect-[2/3] rounded-2xl overflow-hidden border border-border">
        <img src={movie.poster} alt={movie.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
        <div className="absolute top-2 left-2 px-2 py-1 rounded-full bg-black/70 text-white text-xs font-bold flex items-center gap-1">
          <Star className="h-3 w-3 fill-primary text-primary" />
          {movie.rating?.toFixed?.(1) ?? movie.rating}
        </div>
      </div>
      <div className="mt-3">
        <h3 className="font-display font-bold text-base line-clamp-1">{movie.title}</h3>
        <p className="text-xs text-muted-foreground mt-0.5">{movie.language} · {movie.genre}</p>
      </div>
    </button>
  );
}

export function EventCard({ event, isLive = false }) {
  const navigate = useNavigate();
  const eventId = getEventId(event);
  const poster = getEventPoster(event);
  const onClick = () => {
    if (isLive && event.url) {
      window.open(event.url, "_blank", "noopener,noreferrer");
    } else if (eventId) {
      navigate(`/event/${eventId}`);
    }
  };
  return (
    <button
      data-testid={`event-card-${eventId || event.eventbrite_id}`}
      onClick={onClick}
      className="group text-left flex-shrink-0 w-72 md:w-80"
    >
      <div className="relative aspect-[16/10] rounded-2xl overflow-hidden border border-border">
        <img src={poster} alt={event.title} loading="lazy" decoding="async" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" onError={(e) => applyPosterFallback(e, event)} />
        <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/10 to-transparent" />
        {isLive && (
          <span className="absolute top-3 left-3 inline-flex items-center gap-1.5 rounded-full bg-destructive px-2.5 py-1 text-[10px] font-black text-white tracking-wider">
            <span className="relative inline-block h-2 w-2 rounded-full bg-white">
              <span className="absolute inset-0 rounded-full bg-white animate-ping opacity-75" />
            </span>
            LIVE
          </span>
        )}
        <span className="absolute top-3 right-3 rounded-full bg-black/60 backdrop-blur px-2.5 py-1 text-[10px] font-bold text-white">
          {event.language}
        </span>
        <div className="absolute bottom-0 left-0 right-0 p-4 text-white">
          <p className="text-[10px] uppercase tracking-[0.2em] font-bold opacity-80">{event.category} · {event.city}</p>
          <p className="font-display text-lg font-bold line-clamp-2 mt-1">{event.title}</p>
        </div>
      </div>
      <div className="mt-2 px-1 text-xs text-muted-foreground flex items-center justify-between">
        <span>{event.date} · {event.time}</span>
        <span className="font-semibold text-foreground">{event.venue}</span>
      </div>
    </button>
  );
}
